import { useEffect } from 'react';

import { useSearchParams } from 'react-router';

import { OrderingParam, SORT_KEYS, TabConfig } from '../CustomersV2.types';
import { useIsMobileTableLayout } from './useIsMobileTableLayout';
import { TableSettings } from './useTableSettings';

interface CustomersTableState {
  page: number;
  /** Resolved ordering sent to the API — always valid (falls back to defaultSort). */
  sort: OrderingParam;
  /** Raw valid sort from the URL, or null when absent/invalid. Drives header icons. */
  explicitSort: OrderingParam | null;
  filterValue: string | null;
  search: string | null;
}

// Each sort key, ascending and descending — derived from SORT_KEYS so the
// whitelist can't drift from the OrderingParam type.
const ACCEPTED_ORDERINGS: OrderingParam[] = SORT_KEYS.flatMap(
  (key): OrderingParam[] => [key, `-${key}`],
);

const isAcceptedOrdering = (value: string): value is OrderingParam =>
  (ACCEPTED_ORDERINGS as string[]).includes(value);

const filterKeyFor = (config: TabConfig): 'stage' | 'status' =>
  config.filterParam === 'application_stage' ? 'stage' : 'status';

// A filter the toolbar can't clear is treated as absent, whether it names no
// pill on this tab at all (a hand-edited or cross-tab URL) or names one the
// table settings keep hidden. Mirrors the whitelist `sort` gets below: the API
// honours either value, so an unreachable one would filter the table with
// nothing on screen to undo it.
const isUnusableFilter = (
  config: TabConfig,
  settings: TableSettings,
  value: string | null,
): boolean => {
  if (value === null) return false;
  const pill = config.pills.find((candidate) => candidate.value === value);
  if (pill === undefined) return true;
  return pill.revealedBy !== undefined && !settings[pill.revealedBy];
};

/**
 * Strips params the API or the toolbar can't honour, so a shared URL never
 * carries a value the API rejects or a filter nothing on screen can clear.
 *
 * Separate from `useCustomersTableState` because that hook is called once per
 * consumer — tab view, table and tab totals all read the same state. Three
 * copies of this effect would each navigate off their own pre-navigation
 * snapshot of the params, so a write racing them (a pill click on the first
 * render after landing) can be clobbered by a stale snapshot. Call this exactly
 * once, from the component that owns the tab body.
 */
export function useStripUnusableParams(
  config: TabConfig,
  settings: TableSettings,
): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = filterKeyFor(config);

  const rawSort = searchParams.get('sort');
  const hasBadSort = rawSort !== null && !isAcceptedOrdering(rawSort);
  const hasUnusableFilter = isUnusableFilter(
    config,
    settings,
    searchParams.get(filterKey),
  );

  // The mobile layout pages by scrolling from page 1, so a `page` from a shared
  // desktop URL describes neither what's on screen nor anything the seller can
  // change — the page control that writes it isn't rendered.
  const { isMobile } = useIsMobileTableLayout();
  const hasUnusablePage = isMobile && searchParams.has('page');

  useEffect(() => {
    if (!hasBadSort && !hasUnusableFilter && !hasUnusablePage) return;

    setSearchParams(
      (params) => {
        if (hasBadSort) params.delete('sort');
        if (hasUnusableFilter) params.delete(filterKey);
        if (hasUnusablePage) params.delete('page');
        return params;
      },
      { replace: true },
    );
  }, [
    hasBadSort,
    hasUnusableFilter,
    hasUnusablePage,
    filterKey,
    setSearchParams,
  ]);
}

export function useCustomersTableState(
  config: TabConfig,
  settings: TableSettings,
): {
  state: CustomersTableState;
  setPage: (page: number) => void;
  setFilter: (value: string | null) => void;
  setSort: (value: OrderingParam | null) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = filterKeyFor(config);

  const rawSort = searchParams.get('sort');
  const sortIsValid = rawSort !== null && isAcceptedOrdering(rawSort);
  const explicitSort: OrderingParam | null = sortIsValid
    ? (rawSort as OrderingParam)
    : null;
  const sort: OrderingParam = explicitSort ?? config.defaultSort;

  // Resolved on every render, not stripped once by an effect, so no consumer
  // ever builds a request from a filter the toolbar can't clear — including on
  // the render before `useStripUnusableParams` has cleaned the URL.
  const rawFilter = searchParams.get(filterKey);
  const filterValue = isUnusableFilter(config, settings, rawFilter)
    ? null
    : rawFilter;

  const pageParam = Number(searchParams.get('page'));
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const state: CustomersTableState = {
    page,
    sort,
    explicitSort,
    filterValue,
    search: searchParams.get('search'),
  };

  const setPage = (nextPage: number) => {
    setSearchParams((params) => {
      if (nextPage <= 1) params.delete('page');
      else params.set('page', String(nextPage));
      return params;
    });
  };

  const setFilter = (value: string | null) => {
    setSearchParams((params) => {
      if (value) params.set(filterKey, value);
      else params.delete(filterKey);
      params.delete('page');
      return params;
    });
  };

  // Sorting reorders the whole partition, so the current page is meaningless
  // under a new order — reset to page 1, mirroring setFilter.
  const setSort = (value: OrderingParam | null) => {
    setSearchParams((params) => {
      if (value) params.set('sort', value);
      else params.delete('sort');
      params.delete('page');
      return params;
    });
  };

  return { state, setPage, setFilter, setSort };
}
